import { DishItemDisplayType, type LocalDishType } from "@jidlomat/terminal-core/types/mainTypes";
import { Box, type BoxProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { usePriceFormat } from "../../../hooks/formatting/usePriceFormat";
import { PriceWrapper } from "../../global/components/PriceWrapper";
import { DishTypeImage } from "../../primitives/DishTypeImage";

interface OrderListDishTypeItemProps extends BoxProps {
	name: { header: string; subHeader: string } | null;
	pieces: number | undefined;
	dishType: LocalDishType;
	// A refill row carries no price (JID-2360): the count moves up onto the name line and a long name wraps.
	showPrice?: boolean;
}

export const OrderListDishTypeItem = ({
	name,
	pieces,
	dishType,
	showPrice = true,
	...boxProps
}: OrderListDishTypeItemProps) => {
	const { formatPrice, parsePrice } = usePriceFormat();

	const parsedPrice = parsePrice(dishType.price?.current ?? "0");

	return (
		<StyledOrderDishItem {...boxProps}>
			<DishTypeImage imageUrl={dishType.image?.url} className="order" />
			<StyledOrderDishItemContent sx={showPrice ? undefined : { flexDirection: "row", alignItems: "center" }}>
				<Box display="flex" flexDirection="column" minWidth={0}>
					<Typography variant="mealMain" color="white" component="p">
						{name?.header}
					</Typography>
					{/* createSplitDishName moves anything past 28 characters into subHeader: a refill row shows it too. */}
					{!showPrice && name?.subHeader.trim() && (
						<Typography variant="mealSub" color="white" component="p">
							{name.subHeader}
						</Typography>
					)}
				</Box>
				<Box display="flex" justifyContent="space-between" flexShrink={showPrice ? undefined : 0}>
					<Box display="flex" alignItems="center">
						<Typography variant="mealMain">{pieces}</Typography>
						<Typography variant="mealMain" marginInline="8px">
							&times;
						</Typography>
						{showPrice ? <PriceWrapper price={dishType.price} type={DishItemDisplayType.ORDER} /> : null}
					</Box>
					{showPrice && dishType.price?.current && pieces ? (
						<Typography variant="mealMain">{formatPrice(parsedPrice * pieces, dishType.price.currency)}</Typography>
					) : null}
				</Box>
			</StyledOrderDishItemContent>
		</StyledOrderDishItem>
	);
};

export const StyledOrderDishItemContent = styled(Box)(() => ({
	display: "flex",
	flexDirection: "column",
	marginLeft: "70px",
	width: "100%",
	justifyContent: "space-between",
}));

export const StyledOrderDishItem = styled(Box)(({ theme }) => ({
	height: "84px",
	display: "flex",
	position: "relative",
	borderRadius: theme.shape.borderRadius,
}));
